import Joi from 'joi'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../UI/Btn'
import TextAreaField from '../../../../UI/TextAreaField'
import BaseModal from '../../../../UI/BaseModal/BaseModal'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { types } from '../../../../shared/eventEmitter'
import { customNumberFormat } from '../../../../shared/helpers'
import dangerIcon from '../../../../assets/icons/Danger Triangle.svg'

import styles from './style.module.scss'

// Der Grund ist freiwillig — eine Pflichtangabe hielte nur den auf, der die
// Rechnung ohnehin gerade zurückzieht. Wo einer steht, landet er im Verlauf und
// auf dem neu erzeugten PDF.
const schema = Joi.object({
	reason: Joi.string().allow('').max(500)
})

/**
 * Rechnung stornieren (Anforderung #91).
 *
 * Ersetzt das bisherige Löschen über „Bericht zurückweisen": Die Rechnung bleibt
 * mit ihrer Nummer bestehen, zählt nicht mehr zu den offenen Posten, und der
 * Bericht wird zur Neubearbeitung freigegeben — mit den bisherigen Zahlen, damit
 * die Firma nur die falsche Zeile korrigiert.
 */
export default function InvoiceCancel() {
	const { t } = useTranslation()
	const [invoice, setInvoice] = useState(null)
	const [isLoading, setLoading] = useState(false)
	const [onDone, setOnDone] = useState(null)

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, dirtyFields }
	} = useForm({ resolver: joiResolver(schema), defaultValues: { reason: '' } })

	// Was auf die Rechnung schon gezahlt wurde. Beim Storno bleibt der Betrag als
	// Guthaben stehen und wandert auf die naechste Rechnung — wer hier klickt,
	// soll das vorher wissen und nicht erst hinterher (Anforderung #91).
	const gezahlt = Math.round(((invoice?.amount || 0) - (invoice?.balance || 0)) * 100) / 100

	const handleOpen = (item, done = null) => {
		setOnDone(() => done)
		if (!item) return
		setInvoice(item)
		reset({ reason: '' })
	}

	const handleClose = () => {
		reset({ reason: '' })
		setInvoice(null)
	}

	const onSubmit = (form, closeModal) => {
		setLoading(true)
		http.put(TApi.ADMIN_CANCEL_INVOICE(invoice.id), { reason: form.reason || '' })
			.then(() => {
				toast.success(t('The invoice was cancelled'))
				closeModal()
				handleClose()
				if (onDone) onDone()
			})
			.finally(() => setLoading(false))
	}

	return (
		<BaseModal
			onOpen={handleOpen}
			onClose={handleClose}
			nameEvent={types.openInvoiceCancel}
			isDirty={Boolean(Object.keys(dirtyFields).length)}
			title={t('Cancel invoice')}
			isLoading={isLoading}
			maxWidth={620}
		>
			{({ closeModal }) => (
				<form className={styles.root} onSubmit={handleSubmit(form => onSubmit(form, closeModal))}>
					<div className={styles.attentionCard}>
						<img className={styles.warningImage} src={dangerIcon} alt="" />
						<div>
							<h3 className={styles.cardTitle}>
								{t('invoiceCancelHeading', {
									no: invoice?.no || invoice?.id,
									amount: `€ ${customNumberFormat(Math.round((invoice?.amount || 0) * 100) / 100)}`
								})}
							</h3>
							<p className={styles.cardDescription}>{t('invoiceCancelExplain')}</p>
							{gezahlt > 0 && (
								<p className={styles.creditNote}>
									{t('invoiceCancelCreditNote', {
										amount: `€ ${customNumberFormat(gezahlt)}`
									})}
								</p>
							)}
						</div>
					</div>

					<TextAreaField
						mb={25}
						label={t('Reason for cancellation')}
						height={140}
						maxLength={500}
						showLettersLeft
						register={register('reason')}
						error={errors.reason}
						placeholder={t('Reason for cancellation')}
					/>

					<div className={styles.actions}>
						<Btn loading={isLoading} type="submit" className={styles.btnSubmit}>
							{t('Cancel invoice')}
						</Btn>
					</div>
				</form>
			)}
		</BaseModal>
	)
}
